import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  Spinner,
  Button,
  Chip,
  Tabs,
  Tab,
} from '@nextui-org/react';
import {
  BarChart3,
  Trophy,
  Archive,
  ChevronRight,
  ArrowLeft,
  Medal,
  Flame,
  Target,
  TrendingUp,
  CalendarDays,
} from 'lucide-react';
import statsExplorerService, {
  ExplorerTournament,
  ExplorerSeason,
  MssSeries,
  MssSeriesOverview,
} from '../services/statsExplorerService';
import proRankingService, {
  ProRanking,
  ProRankingEntry,
} from '../services/proRankingService';
import PlayerSearchBox from '../components/stats/PlayerSearchBox';
import LeaderboardTable from '../components/stats/LeaderboardTable';

const TEAL = '#3fced1';

// ============================================================
// StatsExplorerPage – /statistiken
// Hero mit Spieler-Suche + Browse-Bereiche:
//   1) Turnier-Archiv (Jahre → Turniere → Saisons)
//   2) Rankings (Top 10, Link zu /ranglisten)
//   3) MODUS Super Series (Serien + Leaderboards)
// ============================================================

const StatsExplorerPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  // MSS-Serien werden früh geladen, damit der Tab nur bei Daten erscheint
  const [mssSeries, setMssSeries] = useState<MssSeries[]>([]);
  const [mssLoaded, setMssLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    statsExplorerService.getMssSeries().then((series) => {
      if (cancelled) return;
      setMssSeries(series);
      setMssLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const showMssTab = mssLoaded && mssSeries.length > 0;

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>Statistik-Explorer | DartCloud</title>
        <meta
          name="description"
          content="Der DartCloud Statistik-Explorer: Historische Ergebnisse, Turnier-Archive, Saison-Statistiken, 180er, High Finishes und Averages aller Profi-Dartspieler durchsuchen."
        />
        <link rel="canonical" href="https://dartcloudapp.de/statistiken" />
      </Helmet>

      {/* Hero mit Spieler-Suche */}
      <div className="border-b border-zinc-900 bg-gradient-to-b from-zinc-900/60 to-zinc-950">
        <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 flex items-center justify-center gap-3">
              <BarChart3 size={40} style={{ color: TEAL }} />
              Statistik-Explorer
            </h1>
            <p className="text-gray-400 mb-8">
              Durchsuche historische Ergebnisse, Turniere und Statistiken aller Profi-Dartspieler
            </p>
            <PlayerSearchBox initialQuery={initialQuery} autoFocus={!!initialQuery} />
            <p className="text-xs text-gray-600 mt-3">
              Tipp: Klicke auf einen Spieler, um sein komplettes Statistik-Profil zu öffnen
            </p>
          </div>
        </div>
      </div>

      {/* Browse-Bereiche */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs
          aria-label="Statistik-Bereiche"
          variant="underlined"
          classNames={{
            tabList: 'gap-6 border-b border-zinc-800 w-full',
            cursor: 'bg-[#3fced1]',
            tab: 'px-0 h-12',
            tabContent: 'group-data-[selected=true]:text-[#3fced1] text-gray-400',
          }}
        >
          <Tab
            key="archiv"
            title={
              <span className="flex items-center gap-2">
                <Archive size={16} /> Turnier-Archiv
              </span>
            }
          >
            <TournamentArchiveSection />
          </Tab>
          <Tab
            key="rankings"
            title={
              <span className="flex items-center gap-2">
                <Trophy size={16} /> Rankings
              </span>
            }
          >
            <RankingsPreviewSection />
          </Tab>
          {showMssTab ? (
            <Tab
              key="mss"
              title={
                <span className="flex items-center gap-2">
                  <Flame size={16} /> MODUS Super Series
                </span>
              }
            >
              <MssSection series={mssSeries} />
            </Tab>
          ) : null}
        </Tabs>
      </div>
    </div>
  );
};

// ============================================================
// 1) Turnier-Archiv
// ============================================================

const TournamentArchiveSection: React.FC = () => {
  const [years, setYears] = useState<number[]>([]);
  const [loadingYears, setLoadingYears] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const [tournaments, setTournaments] = useState<ExplorerTournament[]>([]);
  const [loadingTournaments, setLoadingTournaments] = useState(false);

  const [selectedTournament, setSelectedTournament] = useState<ExplorerTournament | null>(null);
  const [allSeasons, setAllSeasons] = useState<ExplorerSeason[] | null>(null);
  const [loadingSeasons, setLoadingSeasons] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingYears(true);
      const data = await statsExplorerService.getExplorerYears();
      if (cancelled) return;
      setYears(data);
      setLoadingYears(false);
      if (data.length > 0) setSelectedYear(data[0]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    let cancelled = false;
    (async () => {
      setLoadingTournaments(true);
      setSelectedTournament(null);
      setAllSeasons(null);
      const data = await statsExplorerService.getTournamentsByYear(selectedYear);
      if (cancelled) return;
      setTournaments(data);
      setLoadingTournaments(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedYear]);

  const openTournament = useCallback(async (tournament: ExplorerTournament) => {
    setSelectedTournament(tournament);
    setAllSeasons(null);
    if (!tournament.flashliveId) return;
    setLoadingSeasons(true);
    const { seasons } = await statsExplorerService.getTournamentSeasons(tournament.flashliveId);
    setAllSeasons(seasons);
    setLoadingSeasons(false);
  }, []);

  if (loadingYears) {
    return (
      <div className="flex justify-center items-center h-48">
        <Spinner size="lg" style={{ color: TEAL }} />
      </div>
    );
  }

  if (years.length === 0) {
    return (
      <Card className="bg-zinc-900 border border-zinc-800 mt-4">
        <CardBody className="p-10 text-center">
          <Archive size={40} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">Noch keine archivierten Turniere verfügbar</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="pt-4">
      {/* Jahre-Chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        {years.map((year) => {
          const active = year === selectedYear;
          return (
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors border ${
                active
                  ? 'border-transparent text-black'
                  : 'border-zinc-700 text-gray-300 hover:border-zinc-500'
              }`}
              style={active ? { backgroundColor: TEAL } : undefined}
            >
              {year}
            </button>
          );
        })}
      </div>

      {selectedTournament ? (
        // Saison-Auswahl eines Turniers
        <div>
          <Button
            variant="light"
            size="sm"
            startContent={<ArrowLeft size={14} />}
            onPress={() => {
              setSelectedTournament(null);
              setAllSeasons(null);
            }}
            className="text-gray-400 mb-4"
          >
            Zurück zu den Turnieren {selectedYear}
          </Button>
          <h3 className="text-xl font-bold mb-1">
            {selectedTournament.name || 'Turnier'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Saison auswählen, um Statistiken, Ergebnisse und Bracket zu sehen
          </p>

          {loadingSeasons ? (
            <div className="flex justify-center items-center h-32">
              <Spinner size="lg" style={{ color: TEAL }} />
            </div>
          ) : (
            <SeasonList
              seasons={
                allSeasons && allSeasons.length > 0
                  ? allSeasons
                  : selectedTournament.yearSeasons || []
              }
            />
          )}
        </div>
      ) : (
        // Turnier-Grid des Jahres
        <>
          {loadingTournaments ? (
            <div className="flex justify-center items-center h-48">
              <Spinner size="lg" style={{ color: TEAL }} />
            </div>
          ) : tournaments.length === 0 ? (
            <Card className="bg-zinc-900 border border-zinc-800">
              <CardBody className="p-10 text-center">
                <Archive size={40} className="mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400">Keine Turniere für {selectedYear} gefunden</p>
              </CardBody>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tournaments.map((tournament, idx) => (
                <button
                  key={tournament.flashliveId || tournament._id || idx}
                  onClick={() => openTournament(tournament)}
                  className="text-left"
                >
                  <Card className="bg-zinc-900 border border-zinc-800 hover:border-[#3fced1]/50 transition-colors w-full">
                    <CardBody className="p-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${TEAL}14` }}
                        >
                          <Trophy size={18} style={{ color: TEAL }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">
                            {tournament.name || 'Unbekanntes Turnier'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(tournament.yearSeasons || []).length > 0
                              ? `${(tournament.yearSeasons || []).length} Saison(s) in ${selectedYear}`
                              : 'Saisons anzeigen'}
                          </p>
                        </div>
                        <ChevronRight size={16} className="text-gray-600 flex-shrink-0" />
                      </div>
                    </CardBody>
                  </Card>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SeasonList: React.FC<{ seasons: ExplorerSeason[] }> = ({ seasons }) => {
  const safe = (Array.isArray(seasons) ? seasons : []).filter(
    (s) => s && s.seasonTournamentStageId,
  );

  if (safe.length === 0) {
    return (
      <Card className="bg-zinc-900 border border-zinc-800">
        <CardBody className="p-8 text-center">
          <CalendarDays size={32} className="mx-auto text-gray-600 mb-2" />
          <p className="text-gray-400 text-sm">Keine Saisons mit Daten verfügbar</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
      {safe.map((season, idx) => (
        <Link
          key={season.seasonTournamentStageId || idx}
          to={`/statistiken/saison/${encodeURIComponent(season.seasonTournamentStageId as string)}`}
        >
          <Card className="bg-zinc-900 border border-zinc-800 hover:border-[#3fced1]/50 transition-colors">
            <CardBody className="p-3 text-center">
              <p className="text-sm font-semibold text-white">
                {season.seasonName || 'Saison'}
              </p>
              <p className="text-[10px] uppercase tracking-wide mt-1" style={{ color: TEAL }}>
                Öffnen
              </p>
            </CardBody>
          </Card>
        </Link>
      ))}
    </div>
  );
};

// ============================================================
// 2) Rankings-Vorschau (Top 10)
// ============================================================

const RankingsPreviewSection: React.FC = () => {
  const [rankings, setRankings] = useState<ProRanking[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [entries, setEntries] = useState<ProRankingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await proRankingService.getAllRankings();
        if (cancelled) return;
        const sorted = [...data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setRankings(sorted);
        const first = sorted[0];
        const firstId = (first?.id || first?._id) as string | undefined;
        if (firstId) setSelectedId(firstId);
      } catch (err) {
        console.error('Fehler beim Laden der Ranglisten:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    (async () => {
      setLoadingEntries(true);
      try {
        const res = await proRankingService.getRankingEntries(selectedId, 10, 0);
        if (!cancelled) setEntries(res.entries);
      } catch (err) {
        console.error('Fehler beim Laden der Ranking-Einträge:', err);
        if (!cancelled) setEntries([]);
      } finally {
        if (!cancelled) setLoadingEntries(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const selectedRanking = useMemo(
    () => rankings.find((r) => (r.id || r._id) === selectedId) || null,
    [rankings, selectedId],
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <Spinner size="lg" style={{ color: TEAL }} />
      </div>
    );
  }

  if (rankings.length === 0) {
    return (
      <Card className="bg-zinc-900 border border-zinc-800 mt-4">
        <CardBody className="p-10 text-center">
          <Trophy size={40} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">Keine Ranglisten verfügbar</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="pt-4">
      <div className="flex flex-wrap gap-2 mb-4">
        {rankings.map((r) => {
          const id = (r.id || r._id) as string;
          const active = id === selectedId;
          return (
            <button
              key={id}
              onClick={() => setSelectedId(id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                active
                  ? 'border-transparent text-black'
                  : 'border-zinc-700 text-gray-300 hover:border-zinc-500'
              }`}
              style={active ? { backgroundColor: TEAL } : undefined}
            >
              {r.shortName || r.name || 'Rangliste'}
            </button>
          );
        })}
      </div>

      <Card className="bg-zinc-900 border border-zinc-800">
        <CardBody className="p-0">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Medal size={16} style={{ color: TEAL }} />
              {selectedRanking?.name || 'Rangliste'} – Top 10
            </h3>
            {selectedRanking?.dataAsOf && (
              <span className="text-xs text-gray-500">Stand: {selectedRanking.dataAsOf}</span>
            )}
          </div>
          {loadingEntries ? (
            <div className="flex justify-center items-center h-40">
              <Spinner size="lg" style={{ color: TEAL }} />
            </div>
          ) : entries.length === 0 ? (
            <p className="px-4 py-8 text-sm text-gray-500 text-center">Keine Einträge verfügbar</p>
          ) : (
            <ul className="divide-y divide-zinc-800/60">
              {entries.map((entry, idx) => (
                <li key={entry.id || entry._id || idx} className="px-4 py-2.5 flex items-center gap-3">
                  <span
                    className="w-6 text-right text-sm font-bold tabular-nums flex-shrink-0"
                    style={(entry.rank ?? 0) <= 3 ? { color: TEAL } : { color: '#71717a' }}
                  >
                    {entry.rank ?? '-'}
                  </span>
                  <RankedPlayerName entry={entry} />
                  <span className="text-sm font-semibold tabular-nums flex-shrink-0">
                    {proRankingService.formatValue(entry, selectedRanking)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="p-3 border-t border-zinc-800 flex justify-center">
            <Link to={selectedRanking?.slug ? `/ranglisten/${selectedRanking.slug}` : '/ranglisten'}>
              <Button variant="flat" size="sm" className="bg-zinc-800 text-white">
                Komplette Rangliste ansehen
                <ChevronRight size={14} />
              </Button>
            </Link>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

const RankedPlayerName: React.FC<{ entry: ProRankingEntry }> = ({ entry }) => {
  const navigate = useNavigate();
  const name = entry.playerName || 'Unbekannt';
  const flashliveId = entry.flashlivePlayerId;

  const handleClick = () => {
    if (flashliveId) {
      navigate(`/statistiken/spieler/${encodeURIComponent(flashliveId)}`);
    } else {
      navigate(`/statistiken?q=${encodeURIComponent(name)}`);
    }
  };

  return (
    <button
      onClick={handleClick}
      className="flex-1 min-w-0 text-left text-sm text-gray-200 truncate hover:text-[#3fced1] transition-colors"
    >
      {entry.country && (
        <span className="text-[10px] font-bold uppercase text-gray-500 mr-2">{entry.country}</span>
      )}
      {name}
    </button>
  );
};

// ============================================================
// 3) MODUS Super Series
// ============================================================

const MssSection: React.FC<{ series: MssSeries[] }> = ({ series }) => {
  const navigate = useNavigate();
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null);
  const [overview, setOverview] = useState<MssSeriesOverview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);

  const getSeriesId = (s: MssSeries): string | null =>
    s.id != null ? String(s.id) : s._id != null ? String(s._id) : null;
  const getSeriesName = (s: MssSeries): string => s.name || s.seriesName || 'Serie';

  useEffect(() => {
    // Erste Serie automatisch auswählen
    if (!selectedSeriesId && series.length > 0) {
      const firstId = getSeriesId(series[0]);
      if (firstId) setSelectedSeriesId(firstId);
    }
  }, [series, selectedSeriesId]);

  useEffect(() => {
    if (!selectedSeriesId) return;
    let cancelled = false;
    (async () => {
      setLoadingOverview(true);
      const data = await statsExplorerService.getMssSeriesOverview(selectedSeriesId);
      if (cancelled) return;
      setOverview(data);
      setLoadingOverview(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSeriesId]);

  const handlePlayerClick = useCallback(
    async (name: string) => {
      const players = await statsExplorerService.searchPlayers(name, 5);
      const match = players.find((p) => p.flashliveId);
      if (match?.flashliveId) {
        navigate(`/statistiken/spieler/${encodeURIComponent(match.flashliveId)}`);
      } else {
        navigate(`/statistiken?q=${encodeURIComponent(name)}`);
      }
    },
    [navigate],
  );

  return (
    <div className="pt-4">
      <p className="text-sm text-gray-500 mb-4">
        Statistiken aus der MODUS Super Series (DartConnect-Daten)
      </p>
      <div className="flex flex-wrap gap-2 mb-6">
        {series.map((s, idx) => {
          const id = getSeriesId(s);
          const active = id != null && id === selectedSeriesId;
          return (
            <button
              key={id || idx}
              onClick={() => id && setSelectedSeriesId(id)}
              disabled={!id}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                active
                  ? 'border-transparent text-black'
                  : 'border-zinc-700 text-gray-300 hover:border-zinc-500'
              } disabled:opacity-50`}
              style={active ? { backgroundColor: TEAL } : undefined}
            >
              {getSeriesName(s)}
            </button>
          );
        })}
      </div>

      {loadingOverview ? (
        <div className="flex justify-center items-center h-48">
          <Spinner size="lg" style={{ color: TEAL }} />
        </div>
      ) : !overview ? (
        <Card className="bg-zinc-900 border border-zinc-800">
          <CardBody className="p-8 text-center">
            <Flame size={32} className="mx-auto text-gray-600 mb-2" />
            <p className="text-gray-400 text-sm">
              Für diese Serie sind noch keine Leaderboards verfügbar
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <LeaderboardTable
            title="Top Averages"
            icon={<TrendingUp size={16} />}
            rows={overview.topAverages.map((e) => ({
              name: e.name,
              value: e.value,
              sublabel: e.matches != null ? `${e.matches} Matches` : null,
            }))}
            valueLabel="Average"
            decimals={2}
            onPlayerClick={handlePlayerClick}
          />
          <LeaderboardTable
            title="Top 180er"
            icon={<Flame size={16} />}
            rows={overview.top180s.map((e) => ({
              name: e.name,
              value: e.value,
              sublabel: e.matches != null ? `${e.matches} Matches` : null,
            }))}
            valueLabel="180er"
            onPlayerClick={handlePlayerClick}
          />
          <LeaderboardTable
            title="Top Checkouts"
            icon={<Target size={16} />}
            rows={overview.topCheckouts.map((e) => ({
              name: e.name,
              value: e.value,
              sublabel: e.matches != null ? `${e.matches} Matches` : null,
            }))}
            valueLabel="Checkout"
            onPlayerClick={handlePlayerClick}
          />
        </div>
      )}
    </div>
  );
};

export default StatsExplorerPage;
